import React, { useState, useEffect } from 'react';
import api from '../api/api';
import optimizedApi from '../api/optimizedApi';
import imageCompression from 'browser-image-compression';
import { BlurredImage } from '../components/imagePlaceholder';
import { formatNumber, formatTime } from '../utils/dashboardTranslations';

// Add custom CSS for horizontal scrolling
const scrollableImageStyle = `
    .scrollable-images {
        scrollbar-width: thin;
        scrollbar-color: rgba(0,0,0,0.3) transparent;
    }
    .scrollable-images::-webkit-scrollbar {
        height: 4px;
    }
    .scrollable-images::-webkit-scrollbar-track {
        background: transparent;
    }
    .scrollable-images::-webkit-scrollbar-thumb {
        background-color: rgba(0,0,0,0.3);
        border-radius: 2px;
    }
    .scrollable-images::-webkit-scrollbar-thumb:hover {
        background-color: rgba(0,0,0,0.5);
    }
`;

// Inject CSS
if (typeof document !== 'undefined') {
    const styleSheet = document.createElement("style");
    styleSheet.type = "text/css";
    styleSheet.innerText = scrollableImageStyle;
    if (!document.head.querySelector('style[data-scroll-images]')) {
        styleSheet.setAttribute('data-scroll-images', 'true');
        document.head.appendChild(styleSheet);
    }
}

function AllProducts() {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [vendorCategories, setVendorCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddForm, setShowAddForm] = useState(false);
    const [editId, setEditId] = useState(null);
    const [form, setForm] = useState({
        name: '',
        description: '',
        priceInSYP: '',
        costPrice: '',
        discountPrice: '',
        discountPercentage: '',
        discountStartDate: '',
        discountEndDate: '',
        productCategoryId: '',
        vendorCategoryId: '',
        imageFiles: [], // Multiple images upload
        existingImages: [], // Field for existing images when editing
        deliveryTimeMinutes: '',
        colors: [], // New field for product colors with their images
        sizes: [] // New field for product sizes
    });

    // New states for search and category management
    const [searchQuery, setSearchQuery] = useState('');
    const [showCategoryForm, setShowCategoryForm] = useState(false);
    const [editCategoryId, setEditCategoryId] = useState(null);
    const [categoryForm, setCategoryForm] = useState({
        name: '',
        imageFile: null
    });
    const [filteredProducts, setFilteredProducts] = useState([]);

    useEffect(() => {
        fetchAllData();
    }, []);

    useEffect(() => {
        console.log('AllProducts - Categories state updated:', categories.length, 'categories');
        console.log('AllProducts - Form state:', form);
    }, [categories, form]);

    // Filter products based on search query
    useEffect(() => {
        if (!searchQuery.trim()) {
            setFilteredProducts(products);
        } else {
            const filtered = products.filter(product => {
                const searchTerm = searchQuery.toLowerCase();
                return (
                    product.name.toLowerCase().includes(searchTerm) ||
                    product.description?.toLowerCase().includes(searchTerm) ||
                    product.productCategory?.name.toLowerCase().includes(searchTerm) ||
                    product.vendorCategory?.name.toLowerCase().includes(searchTerm)
                );
            });
            setFilteredProducts(filtered);
        }
    }, [products, searchQuery]);

    const fetchAllData = async () => {
        await Promise.all([
            fetchProducts(),
            fetchCategories(),
            fetchVendorCategories()
        ]);
        setLoading(false);
    };

    const fetchProducts = async () => {
        try {
            console.log('Fetching standalone products from API...');
            
            // Get all products directly from the new endpoint
            try {
                const response = await api.get('/Product');
                console.log('Direct product fetch successful:', response.data.length, 'products');
                
                // Filter to show only standalone products (products without vendors)
                const standaloneProducts = response.data.filter(product => 
                    !product.vendorId && !product.vendor
                );
                
                console.log('Filtered standalone products:', standaloneProducts.length);
                
                // Fetch product categories and vendor categories in parallel
                try {
                    const [categoriesResponse, vendorCategoriesResponse] = await Promise.all([
                        optimizedApi.getStandaloneProductCategories(), // Use standalone categories
                        api.get('/VendorCategory')
                    ]);
                    
                    const categoriesMap = {};
                    const categoriesData = Array.isArray(categoriesResponse.data) 
                        ? categoriesResponse.data 
                        : (categoriesResponse.data?.categories || []);
                    
                    categoriesData.forEach(cat => {
                        categoriesMap[cat.id] = cat;
                    });
                    
                    const vendorCategoriesMap = {};
                    const vendorCategoriesData = Array.isArray(vendorCategoriesResponse.data) 
                        ? vendorCategoriesResponse.data 
                        : (vendorCategoriesResponse.data?.categories || []);
                    
                    vendorCategoriesData.forEach(cat => {
                        vendorCategoriesMap[cat.id] = cat;
                    });
                    
                    // Add category information to each standalone product
                    const productsWithCategories = standaloneProducts.map(product => ({
                        ...product,
                        productCategory: categoriesMap[product.productCategoryId] || null,
                        vendorCategory: vendorCategoriesMap[product.vendorCategoryId] || null
                    }));
                    
                    console.log('Product and vendor categories mapped successfully for standalone products');
                    setProducts(productsWithCategories);
                } catch (categoryError) {
                    console.log('Failed to fetch categories:', categoryError.message);
                    setProducts(standaloneProducts);
                }
                
            } catch (directError) {
                console.log('Direct product fetch failed:', directError.message);
                console.error('Error fetching standalone products:', directError);
                alert('فشل في تحميل المنتجات المستقلة من الخادم');
                setProducts([]);
            }
            
        } catch (error) {
            console.error('Error fetching products:', error);
            alert('فشل في تحميل المنتجات من الخادم');
            setProducts([]);
        }
    };

    const fetchCategories = async () => {
        try {
            console.log('Fetching standalone product categories...');
            const response = await optimizedApi.getStandaloneProductCategories();
            console.log('AllProducts - Standalone categories loaded:', response.data);
            setCategories(response.data);
        } catch (error) {
            console.error('Error fetching standalone categories:', error);
            // Fallback to all categories and filter client-side
            try {
                const response = await optimizedApi.getProductCategories();
                const standaloneCategories = response.data.filter(cat => cat.vendorId === null || cat.vendorId === undefined);
                console.log('AllProducts - Filtered standalone categories:', standaloneCategories);
                setCategories(standaloneCategories);
            } catch (fallbackError) {
                console.error('Error fetching categories fallback:', fallbackError);
                setCategories([]);
            }
        }
    };

    const fetchVendorCategories = async () => {
        try {
            const response = await api.get('/VendorCategory');
            console.log('AllProducts - Vendor categories response:', response.data);
            
            // Handle different response structures
            if (Array.isArray(response.data)) {
                setVendorCategories(response.data);
            } else if (response.data && response.data.categories && Array.isArray(response.data.categories)) {
                setVendorCategories(response.data.categories);
            } else {
                console.error('Unexpected vendor categories response structure:', response.data);
                setVendorCategories([]);
            }
        } catch (error) {
            console.error('Error fetching vendor categories:', error);
            setVendorCategories([]);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            console.log('Submitting standalone product form:', form);

            if (form.discountPrice && form.discountPercentage) {
                alert('لا يمكن إدخال سعر خصم ونسبة خصم معاً. اختر أحدهما فقط.');
                return;
            }

            const basePrice = Number(form.priceInSYP || 0);
            const discountPrice = Number(form.discountPrice || 0);
            const discountPercentage = Number(form.discountPercentage || 0);

            if (form.discountPrice && discountPrice >= basePrice) {
                alert('سعر الخصم يجب أن يكون أقل من السعر الأساسي.');
                return;
            }

            if (form.discountPercentage && (discountPercentage <= 0 || discountPercentage >= 100)) {
                alert('نسبة الخصم يجب أن تكون بين 1 و 99.');
                return;
            }

            if (form.discountStartDate && form.discountEndDate && new Date(form.discountEndDate) <= new Date(form.discountStartDate)) {
                alert('تاريخ نهاية الخصم يجب أن يكون بعد تاريخ البداية.');
                return;
            }

            const formData = new FormData();
            formData.append('Name', form.name);
            formData.append('Description', form.description);
            formData.append('PriceInSYP', form.priceInSYP);
            formData.append('CostPrice', form.costPrice);
            formData.append('ProductCategoryId', form.productCategoryId);
            if (form.deliveryTimeMinutes) {
                formData.append('DeliveryTimeMinutes', form.deliveryTimeMinutes);
            }
            if (form.vendorCategoryId) {
                formData.append('VendorCategoryId', form.vendorCategoryId);
            }
            if (form.discountPrice) {
                formData.append('DiscountPrice', form.discountPrice);
            }
            if (form.discountPercentage) {
                formData.append('DiscountPercentage', form.discountPercentage);
            }
            if (form.discountStartDate) {
                formData.append('DiscountStartDate', form.discountStartDate);
            }
            if (form.discountEndDate) {
                formData.append('DiscountEndDate', form.discountEndDate);
            }

            let productId;
            if (editId) {
                // Update existing standalone product
                console.log('Updating standalone product:', editId);
                await api.put(`/Product/${editId}`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                productId = editId;
                alert('تم تحديث المنتج المستقل بنجاح');
            } else {
                // Create new standalone product
                console.log('Creating new standalone product');
                const response = await api.post('/Product/standalone/with-image', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                productId = response.data.id;
                alert('تم إضافة المنتج المستقل بنجاح');
            }

            // Upload multiple images if provided
            if (form.imageFiles.length > 0) {
                console.log('Uploading multiple images for product:', productId);
                const imageFormData = new FormData();
                form.imageFiles.forEach((file, index) => {
                    imageFormData.append('images', file);
                });

                try {
                    await api.post(`/Product/${productId}/images`, imageFormData, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                    });
                    console.log('Multiple images uploaded successfully');
                } catch (imageError) {
                    console.error('Error uploading multiple images:', imageError);
                    alert('تم حفظ المنتج ولكن فشل في رفع بعض الصور');
                }
            }

            // Handle product colors if provided
            if (form.colors && form.colors.length > 0) {
                console.log('Processing product colors:', productId, form.colors);
                
                for (const color of form.colors) {
                    if (color.colorName.trim()) {
                        // Check if this is an existing color (has a proper database ID)
                        if (color.id && typeof color.id === 'number' && color.id > 0 && !color.id.toString().includes('.')) {
                            // Update existing color
                            try {
                                await api.put(`/Product/${productId}/colors/${color.id}`, {
                                    id: color.id,
                                    colorName: color.colorName,
                                    colorHex: color.colorHex || '',
                                    isDefault: color.isDefault,
                                    colorImages: [] // Include empty array for colorImages as expected by DTO
                                });
                                console.log('Existing color updated successfully:', color.colorName);
                            } catch (colorError) {
                                console.error('Error updating existing color:', color.colorName, colorError);
                                console.error('Update error details:', colorError.response?.data);
                                alert(`تم حفظ المنتج ولكن فشل في تحديث لون: ${color.colorName}`);
                            }

                            // Process color sizes for existing color
                            if (color.sizes && color.sizes.length > 0) {
                                for (const size of color.sizes) {
                                    if (size.sizeName.trim()) {
                                        if (size.id && typeof size.id === 'number' && size.id > 0 && !size.id.toString().includes('.')) {
                                            // Update existing color size
                                            try {
                                                await api.put(`/Product/${productId}/colors/${color.id}/sizes/${size.id}`, {
                                                    id: size.id,
                                                    sizeName: size.sizeName,
                                                    sizeCode: size.sizeCode || '',
                                                    sizeDescription: size.sizeDescription || '',
                                                    isDefault: size.isDefault,
                                                    order: size.order || 1
                                                });
                                                console.log('Existing color size updated successfully:', size.sizeName);
                                            } catch (sizeError) {
                                                console.error('Error updating existing color size:', size.sizeName, sizeError);
                                                alert(`فشل في تحديث مقاس اللون: ${size.sizeName}`);
                                            }
                                        } else {
                                            // Add new color size
                                            try {
                                                await api.post(`/Product/${productId}/colors/${color.id}/sizes`, {
                                                    sizeName: size.sizeName,
                                                    sizeCode: size.sizeCode || '',
                                                    sizeDescription: size.sizeDescription || '',
                                                    isDefault: size.isDefault,
                                                    order: size.order || 1
                                                });
                                                console.log('New color size added successfully:', size.sizeName);
                                            } catch (sizeError) {
                                                console.error('Error adding new color size:', size.sizeName, sizeError);
                                                alert(`فشل في إضافة مقاس جديد للون: ${size.sizeName}`);
                                            }
                                        }
                                    }
                                }
                            }
                        } else {
                            // Add new color
                            const colorFormData = new FormData();
                            colorFormData.append('ColorName', color.colorName);
                            if (color.colorHex) {
                                colorFormData.append('ColorHex', color.colorHex);
                            }
                            colorFormData.append('IsDefault', color.isDefault);
                            
                            // Add color images
                            if (color.images && color.images.length > 0) {
                                color.images.forEach((image, index) => {
                                    colorFormData.append('Images', image);
                                });
                            }

                            try {
                                const response = await api.post(`/Product/${productId}/colors`, colorFormData, {
                                    headers: { 'Content-Type': 'multipart/form-data' }
                                });
                                console.log('New color added successfully:', color.colorName);
                                
                                // Get the new color ID from response to save sizes
                                if (response.data && response.data.colorId) {
                                    color.id = response.data.colorId;
                                }
                            } catch (colorError) {
                                console.error('Error adding new color:', color.colorName, colorError);
                                console.error('Add color error details:', colorError.response?.data);
                                alert(`تم حفظ المنتج ولكن فشل في إضافة لون جديد: ${color.colorName}`);
                            }
                        }

                        // Process color sizes if any and if we have a color ID
                        if (color.sizes && color.sizes.length > 0 && color.id) {
                            for (const size of color.sizes) {
                                if (size.sizeName.trim()) {
                                    if (size.id && typeof size.id === 'number' && size.id > 0 && !size.id.toString().includes('.')) {
                                        // Update existing color size
                                        try {
                                            await api.put(`/Product/${productId}/colors/${color.id}/sizes/${size.id}`, {
                                                id: size.id,
                                                sizeName: size.sizeName,
                                                sizeCode: size.sizeCode || '',
                                                sizeDescription: size.sizeDescription || '',
                                                isDefault: size.isDefault,
                                                order: size.order || 1
                                            });
                                            console.log('Existing color size updated successfully:', size.sizeName);
                                        } catch (sizeError) {
                                            console.error('Error updating existing color size:', size.sizeName, sizeError);
                                            alert(`فشل في تحديث مقاس اللون: ${size.sizeName}`);
                                        }
                                    } else {
                                        // Add new color size
                                        try {
                                            await api.post(`/Product/${productId}/colors/${color.id}/sizes`, {
                                                sizeName: size.sizeName,
                                                sizeCode: size.sizeCode || '',
                                                sizeDescription: size.sizeDescription || '',
                                                isDefault: size.isDefault,
                                                order: size.order || 1
                                            });
                                            console.log('New color size added successfully:', size.sizeName);
                                        } catch (sizeError) {
                                            console.error('Error adding new color size:', size.sizeName, sizeError);
                                            alert(`فشل في إضافة مقاس جديد للون: ${size.sizeName}`);
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }

            // Handle product sizes if provided
            if (form.sizes && form.sizes.length > 0) {
                console.log('Processing product sizes:', productId, form.sizes);
                
                for (const size of form.sizes) {
                    if (size.sizeName.trim()) {
                        // Check if this is an existing size (has a proper database ID)
                        if (size.id && typeof size.id === 'number' && size.id > 0 && !size.id.toString().includes('.')) {
                            // Update existing size
                            try {
                                await api.put(`/Product/${productId}/sizes/${size.id}`, {
                                    id: size.id,
                                    sizeName: size.sizeName,
                                    sizeCode: size.sizeCode || '',
                                    sizeDescription: size.sizeDescription || '',
                                    isDefault: size.isDefault,
                                    order: size.order || 1
                                });
                                console.log('Existing size updated successfully:', size.sizeName);
                            } catch (sizeError) {
                                console.error('Error updating existing size:', size.sizeName, sizeError);
                                console.error('Update error details:', sizeError.response?.data);
                                alert(`تم حفظ المنتج ولكن فشل في تحديث مقاس: ${size.sizeName}`);
                            }
                        } else {
                            // Add new size
                            try {
                                await api.post(`/Product/${productId}/sizes`, {
                                    sizeName: size.sizeName,
                                    sizeCode: size.sizeCode || '',
                                    sizeDescription: size.sizeDescription || '',
                                    isDefault: size.isDefault,
                                    order: size.order || 1
                                });
                                console.log('New size added successfully:', size.sizeName);
                            } catch (sizeError) {
                                console.error('Error adding new size:', size.sizeName, sizeError);
                                console.error('Add size error details:', sizeError.response?.data);
                                alert(`تم حفظ المنتج ولكن فشل في إضافة مقاس جديد: ${size.sizeName}`);
                            }
                        }
                    }
                }
            }

            resetForm();
            fetchProducts();
        } catch (error) {
            console.error('Error saving standalone product:', error);
            console.error('Error details:', error.response?.data);
            alert(`فشل في حفظ المنتج المستقل: ${error.response?.data?.message || error.message}`);
        }
    };

    const handleEdit = (product) => {
        console.log('Editing standalone product:', product);
        setForm({
            name: product.name,
            description: product.description || '',
            priceInSYP: product.priceInSYP,
            costPrice: product.costPrice || '',
            discountPrice: product.discountPrice ?? '',
            discountPercentage: product.discountPercentage ?? '',
            discountStartDate: product.discountStartDate ? String(product.discountStartDate).slice(0, 10) : '',
            discountEndDate: product.discountEndDate ? String(product.discountEndDate).slice(0, 10) : '',
            productCategoryId: product.productCategoryId || '',
            vendorCategoryId: product.vendorCategoryId || '',
            imageFiles: [],
            existingImages: product.productImages || [], // Load existing images for editing
            deliveryTimeMinutes: product.deliveryTimeMinutes || '',
            colors: (product.productColors || []).map(color => ({
                id: color.id, // Keep the database ID for existing colors
                colorName: color.colorName,
                colorHex: color.colorHex,
                isDefault: color.isDefault,
                images: [], // Start with empty images array for existing colors (images are displayed separately)
                sizes: (color.colorSizes || []).map(size => ({
                    id: size.id,
                    sizeName: size.sizeName,
                    sizeCode: size.sizeCode || '',
                    sizeDescription: size.sizeDescription || '',
                    isDefault: size.isDefault,
                    order: size.order || 1
                }))
            })),
            sizes: (product.productSizes || []).map(size => ({
                id: size.id, // Keep the database ID for existing sizes
                sizeName: size.sizeName,
                sizeCode: size.sizeCode || '',
                sizeDescription: size.sizeDescription || '',
                isDefault: size.isDefault,
                order: size.order || 1
            }))
        });
        setEditId(product.id);
        setShowAddForm(true);
    };

    const handleDelete = async (product) => {
        if (window.confirm('هل أنت متأكد من حذف هذا المنتج المستقل؟')) {
            try {
                console.log('Deleting standalone product:', product.id);
                await api.delete(`/Product/${product.id}`);
                alert('تم حذف المنتج المستقل بنجاح');
                fetchProducts();
            } catch (error) {
                console.error('Error deleting standalone product:', error);
                alert('فشل في حذف المنتج المستقل');
            }
        }
    };

    const handleToggleAvailability = async (product) => {
        try {
            const res = await api.put(`/Product/${product.id}/toggle-availability`);
            // Update the product in local state without refetching
            setProducts(prev => prev.map(p =>
                p.id === product.id ? { ...p, isAvailable: res.data.isAvailable } : p
            ));
        } catch (error) {
            console.error('Error toggling availability:', error);
            alert('فشل في تغيير حالة المنتج');
        }
    };

    const resetForm = () => {
        setForm({
            name: '',
            description: '',
            priceInSYP: '',
            costPrice: '',
            discountPrice: '',
            discountPercentage: '',
            discountStartDate: '',
            discountEndDate: '',
            productCategoryId: '',
            vendorCategoryId: '',
            imageFiles: [],
            existingImages: [],
            deliveryTimeMinutes: '',
            colors: [],
            sizes: []
        });
        setEditId(null);
        setShowAddForm(false);
    };

    // Image compression helper
    const compressImage = async (file) => {
        try {
            const options = {
                maxSizeMB: 0.3,
                maxWidthOrHeight: 800,
                useWebWorker: true
            };
            const compressedFile = await imageCompression(file, options);
            console.log('Original size:', (file.size / 1024 / 1024).toFixed(2), 'MB');
            console.log('Compressed size:', (compressedFile.size / 1024 / 1024).toFixed(2), 'MB');
            return compressedFile;
        } catch (error) {
            console.error('Error compressing image:', error);
            return file; // Return original file if compression fails
        }
    };

    // Handle multiple images upload
    const handleMultipleImagesChange = async (e) => {
        if (e.target.files && e.target.files.length > 0) {
            const files = Array.from(e.target.files);
            
            // Validate file types
            for (const file of files) {
                if (!file.type.startsWith('image/')) {
                    alert(`الملف ${file.name} ليس صورة صحيحة`);
                    return;
                }
                
                // Validate file size (10MB max before compression)
                if (file.size > 10 * 1024 * 1024) {
                    alert(`الصورة ${file.name} كبيرة جداً. يرجى اختيار صورة أصغر من 10 ميجابايت`);
                    return;
                }
            }
            
            try {
                // Compress all images
                const compressedImages = await Promise.all(
                    files.map(file => compressImage(file))
                );
                
                setForm(prev => ({ 
                    ...prev, 
                    imageFiles: [...prev.imageFiles, ...compressedImages] 
                }));
            } catch (error) {
                console.error('Error processing images:', error);
                alert('فشل في معالجة الصور');
            }
        }
    };

    // Remove image from multiple images array
    const removeImage = (index) => {
        setForm(prev => ({
            ...prev,
            imageFiles: prev.imageFiles.filter((_, i) => i !== index)
        }));
    };

    // Delete existing product image from database
    const deleteExistingImage = async (imageId) => {
        if (!editId || !imageId) return;
        
        if (window.confirm('هل أنت متأكد من حذف هذه الصورة؟')) {
            try {
                await api.delete(`/Product/${editId}/images/${imageId}`);
                console.log('Image deleted successfully from database');
                
                // Refresh the product data to update the UI
                fetchProducts();
                
                // Find the updated product and refresh the form
                const response = await api.get('/Product');
                const updatedProduct = response.data.find(p => p.id === editId);
                if (updatedProduct) {
                    setForm(prev => ({
                        ...prev,
                        existingImages: updatedProduct.productImages || []
                    }));
                }
                
                alert('تم حذف الصورة بنجاح');
            } catch (error) {
                console.error('Error deleting image:', error);
                alert('فشل في حذف الصورة');
            }
        }
    };

    // Color management functions
    const addNewColor = () => {
        setForm(prev => ({
            ...prev,
            colors: [...(prev.colors || []), {
                // Don't assign any ID for new colors - let backend create them
                colorName: '',
                colorHex: '',
                isDefault: !prev.colors || prev.colors.length === 0, // First color is default
                images: [],
                sizes: [] // Add sizes array for each color
            }]
        }));
    };

    const removeColor = async (colorIndex) => {
        if (window.confirm('هل أنت متأكد من حذف هذا اللون؟')) {
            console.log('Removing color at index:', colorIndex, 'Current colors:', form.colors);
            
            const currentColors = form.colors || [];
            const colorToRemove = currentColors[colorIndex];
            
            // If this is an existing color (has a proper database ID), delete it from the database
            if (colorToRemove && colorToRemove.id && typeof colorToRemove.id === 'number' && colorToRemove.id > 0 && !colorToRemove.id.toString().includes('.') && editId) {
                try {
                    console.log('Deleting color from database:', colorToRemove.id, 'Product:', editId);
                    await api.delete(`/Product/${editId}/colors/${colorToRemove.id}`);
                    console.log('Color deleted successfully from database');
                } catch (error) {
                    console.error('Error deleting color from database:', error);
                    alert('فشل في حذف اللون من قاعدة البيانات');
                    return; // Don't update local state if database deletion failed
                }
            }
            
            // Update local state
            setForm(prev => {
                const currentColors = prev.colors || [];
                const colorToRemove = currentColors[colorIndex];
                const newColors = currentColors.filter((_, index) => index !== colorIndex);
                
                // If we removed the default color and there are still colors left,
                // make the first remaining color the default
                if (colorToRemove && colorToRemove.isDefault && newColors.length > 0) {
                    newColors[0].isDefault = true;
                }
                
                console.log('New colors after removal:', newColors);
                return {
                    ...prev,
                    colors: newColors
                };
            });
        }
    };

    const updateColor = (colorIndex, field, value) => {
        setForm(prev => ({
            ...prev,
            colors: (prev.colors || []).map((color, index) => 
                index === colorIndex ? { ...color, [field]: value } : color
            )
        }));
    };

    const setDefaultColor = (colorIndex) => {
        setForm(prev => ({
            ...prev,
            colors: (prev.colors || []).map((color, index) => ({
                ...color,
                isDefault: index === colorIndex
            }))
        }));
    };

    const addColorImages = async (colorIndex, files) => {
        if (!files || files.length === 0) return;
        
        try {
            // Compress all images
            const compressedImages = await Promise.all(
                files.map(file => compressImage(file))
            );
            
            setForm(prev => ({
                ...prev,
                colors: (prev.colors || []).map((color, index) => 
                    index === colorIndex 
                        ? { ...color, images: [...(color.images || []), ...compressedImages] }
                        : color
                )
            }));
        } catch (error) {
            console.error('Error processing color images:', error);
            alert('فشل في معالجة صور اللون');
        }
    };

    const removeColorImage = (colorIndex, imageIndex) => {
        setForm(prev => ({
            ...prev,
            colors: (prev.colors || []).map((color, index) => 
                index === colorIndex 
                    ? { ...color, images: (color.images || []).filter((_, imgIndex) => imgIndex !== imageIndex) }
                    : color
            )
        }));
    };

    // Color-specific size management functions
    const addColorSize = (colorIndex) => {
        setForm(prev => ({
            ...prev,
            colors: (prev.colors || []).map((color, index) => 
                index === colorIndex 
                    ? { 
                        ...color, 
                        sizes: [...(color.sizes || []), {
                            sizeName: '',
                            sizeCode: '',
                            sizeDescription: '',
                            isDefault: !color.sizes || color.sizes.length === 0,
                            order: (color.sizes || []).length + 1
                        }]
                    }
                    : color
            )
        }));
    };

    const removeColorSize = async (colorIndex, sizeIndex) => {
        if (window.confirm('هل أنت متأكد من حذف هذا المقاس؟')) {
            const currentColors = form.colors || [];
            const color = currentColors[colorIndex];
            const sizeToRemove = color?.sizes?.[sizeIndex];
            
            // If this is an existing size (has a proper database ID), delete it from the database
            if (sizeToRemove && sizeToRemove.id && typeof sizeToRemove.id === 'number' && sizeToRemove.id > 0 && !sizeToRemove.id.toString().includes('.') && editId && color.id) {
                try {
                    await api.delete(`/Product/${editId}/colors/${color.id}/sizes/${sizeToRemove.id}`);
                } catch (error) {
                    console.error('Error deleting color size from database:', error);
                    alert('فشل في حذف المقاس من قاعدة البيانات');
                    return;
                }
            }
            
            setForm(prev => ({
                ...prev,
                colors: (prev.colors || []).map((color, index) => 
                    index === colorIndex 
                        ? { 
                            ...color, 
                            sizes: (color.sizes || []).filter((_, idx) => idx !== sizeIndex)
                        }
                        : color
                )
            }));
        }
    };

    const updateColorSize = (colorIndex, sizeIndex, field, value) => {
        setForm(prev => ({
            ...prev,
            colors: (prev.colors || []).map((color, index) => 
                index === colorIndex 
                    ? { 
                        ...color, 
                        sizes: (color.sizes || []).map((size, idx) => 
                            idx === sizeIndex ? { ...size, [field]: value } : size
                        )
                    }
                    : color
            )
        }));
    };

    const setDefaultColorSize = (colorIndex, sizeIndex) => {
        setForm(prev => ({
            ...prev,
            colors: (prev.colors || []).map((color, index) => 
                index === colorIndex 
                    ? { 
                        ...color, 
                        sizes: (color.sizes || []).map((size, idx) => ({
                            ...size,
                            isDefault: idx === sizeIndex
                        }))
                    }
                    : color
            )
        }));
    };

    // Size management functions
    const addNewSize = () => {
        setForm(prev => ({
            ...prev,
            sizes: [...(prev.sizes || []), {
                // Don't assign any ID for new sizes - let backend create them
                sizeName: '',
                sizeCode: '',
                sizeDescription: '',
                isDefault: !prev.sizes || prev.sizes.length === 0, // First size is default
                order: (prev.sizes || []).length + 1
            }]
        }));
    };

    const removeSize = async (sizeIndex) => {
        if (window.confirm('هل أنت متأكد من حذف هذا المقاس؟')) {
            console.log('Removing size at index:', sizeIndex, 'Current sizes:', form.sizes);
            
            const currentSizes = form.sizes || [];
            const sizeToRemove = currentSizes[sizeIndex];
            
            // If this is an existing size (has a proper database ID), delete it from the database
            if (sizeToRemove && sizeToRemove.id && typeof sizeToRemove.id === 'number' && sizeToRemove.id > 0 && !sizeToRemove.id.toString().includes('.') && editId) {
                try {
                    console.log('Deleting size from database:', sizeToRemove.id, 'Product:', editId);
                    await api.delete(`/Product/${editId}/sizes/${sizeToRemove.id}`);
                    console.log('Size deleted successfully from database');
                } catch (error) {
                    console.error('Error deleting size from database:', error);
                    alert('فشل في حذف المقاس من قاعدة البيانات');
                    return; // Don't update local state if database deletion failed
                }
            }
            
            // Update local state
            setForm(prev => {
                const currentSizes = prev.sizes || [];
                const sizeToRemove = currentSizes[sizeIndex];
                const newSizes = currentSizes.filter((_, index) => index !== sizeIndex);
                
                // If we removed the default size and there are still sizes left,
                // make the first remaining size the default
                if (sizeToRemove && sizeToRemove.isDefault && newSizes.length > 0) {
                    newSizes[0].isDefault = true;
                }
                
                // Update order for remaining sizes
                newSizes.forEach((size, index) => {
                    size.order = index + 1;
                });
                
                console.log('New sizes after removal:', newSizes);
                return {
                    ...prev,
                    sizes: newSizes
                };
            });
        }
    };

    const updateSize = (sizeIndex, field, value) => {
        setForm(prev => ({
            ...prev,
            sizes: (prev.sizes || []).map((size, index) => 
                index === sizeIndex ? { ...size, [field]: value } : size
            )
        }));
    };

    const setDefaultSize = (sizeIndex) => {
        setForm(prev => ({
            ...prev,
            sizes: (prev.sizes || []).map((size, index) => ({
                ...size,
                isDefault: index === sizeIndex
            }))
        }));
    };

    const moveSizeUp = (sizeIndex) => {
        if (sizeIndex === 0) return; // Can't move first item up
        
        setForm(prev => {
            const newSizes = [...(prev.sizes || [])];
            const currentSize = newSizes[sizeIndex];
            const previousSize = newSizes[sizeIndex - 1];
            
            // Swap positions
            newSizes[sizeIndex - 1] = currentSize;
            newSizes[sizeIndex] = previousSize;
            
            // Update order values
            newSizes.forEach((size, index) => {
                size.order = index + 1;
            });
            
            return {
                ...prev,
                sizes: newSizes
            };
        });
    };

    const moveSizeDown = (sizeIndex) => {
        setForm(prev => {
            const currentSizes = prev.sizes || [];
            if (sizeIndex >= currentSizes.length - 1) return prev; // Can't move last item down
            
            const newSizes = [...currentSizes];
            const currentSize = newSizes[sizeIndex];
            const nextSize = newSizes[sizeIndex + 1];
            
            // Swap positions
            newSizes[sizeIndex + 1] = currentSize;
            newSizes[sizeIndex] = nextSize;
            
            // Update order values
            newSizes.forEach((size, index) => {
                size.order = index + 1;
            });
            
            return {
                ...prev,
                sizes: newSizes
            };
        });
    };

    // Category management functions
    const handleCategorySubmit = async (e) => {
        e.preventDefault();
        try {
            let imageUrl = '';
            if (categoryForm.imageFile) {
                const imgFormData = new FormData();
                imgFormData.append('Image', categoryForm.imageFile);
                try {
                    const res = await api.post('/Upload', imgFormData, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                    });
                    imageUrl = res.data.url || res.data.imageUrl;
                } catch {
                    alert('فشل رفع الصورة');
                    return;
                }
            }
            
            const categoryData = {
                name: categoryForm.name,
                imageUrl,
                vendorId: null // null for standalone products
            };

            console.log('Submitting category data:', categoryData);

            if (editCategoryId) {
                await api.put(`/ProductCategory/${editCategoryId}`, categoryData, {
                    headers: { 'Content-Type': 'application/json' }
                });
                alert('تم تحديث الفئة بنجاح');
            } else {
                await api.post('/ProductCategory', categoryData, {
                    headers: { 'Content-Type': 'application/json' }
                });
                alert('تم إضافة الفئة بنجاح');
            }

            resetCategoryForm();
            fetchCategories();
        } catch (error) {
            console.error('Error saving category:', error);
            console.error('Error response:', error.response?.data);
            alert(`فشل في حفظ الفئة: ${error.response?.data?.message || error.message}`);
        }
    };

    const handleEditCategory = (category) => {
        setCategoryForm({
            name: category.name,
            imageFile: null
        });
        setEditCategoryId(category.id);
        setShowCategoryForm(true);
    };

    const handleDeleteCategory = async (category) => {
        if (window.confirm('هل أنت متأكد من حذف هذه الفئة؟')) {
            try {
                await api.delete(`/ProductCategory/${category.id}`);
                alert('تم حذف الفئة بنجاح');
                fetchCategories();
            } catch (error) {
                console.error('Error deleting category:', error);
                alert('فشل في حذف الفئة');
            }
        }
    };

    const resetCategoryForm = () => {
        setCategoryForm({
            name: '',
            imageFile: null
        });
        setEditCategoryId(null);
        setShowCategoryForm(false);
    };

    const handleCategoryImageChange = async (e) => {
        if (e.target.files && e.target.files[0]) {
            try {
                const compressed = await imageCompression(e.target.files[0], {
                    maxSizeMB: 0.3,
                    maxWidthOrHeight: 800,
                    useWebWorker: true
                });
                setCategoryForm({ ...categoryForm, imageFile: compressed });
            } catch (error) {
                console.error('Error compressing image:', error);
                setCategoryForm({ ...categoryForm, imageFile: e.target.files[0] });
            }
        }
    };

    const getImageUrl = (imageUrl) => {
        if (!imageUrl) return '/placeholder-image.png';
        if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) return imageUrl.replace(':5000', '');
        
        // Try to construct URL that works with production server
        const apiBase = (process.env.REACT_APP_API_URL || '').replace('/api', '');
        
        // If imageUrl starts with /uploads or /wwwroot, use it directly
        if (imageUrl.startsWith('/uploads') || imageUrl.startsWith('/wwwroot')) {
            const finalUrl = apiBase + imageUrl;
            console.log('Image URL (with path):', finalUrl);
            return finalUrl;
        }
        
        // If it's just a filename, assume it's in uploads folder
        if (!imageUrl.startsWith('/')) {
            const finalUrl = apiBase + '/uploads/' + imageUrl;
            console.log('Image URL (filename only):', finalUrl);
            return finalUrl;
        }
        
        const finalUrl = apiBase + imageUrl;
        console.log('Image URL (default):', finalUrl);
        return finalUrl;
    };

    // Helper function to get images to display for a product (product images or color images as fallback)
    const getDisplayImages = (product) => {
        // If product has regular images, use them
        if (product.productImages && product.productImages.length > 0) {
            return { 
                images: product.productImages, 
                type: 'product',
                source: 'product images'
            };
        }
        
        // If no product images but has color images, use color images
        if (product.productColors && product.productColors.length > 0) {
            const colorImages = [];
            product.productColors.forEach(color => {
                if (color.colorImages && color.colorImages.length > 0) {
                    // Add color name to image data for better identification
                    const colorImagesWithName = color.colorImages.map(img => ({
                        ...img,
                        colorName: color.colorName,
                        colorHex: color.colorHex,
                        isDefault: color.isDefault
                    }));
                    colorImages.push(...colorImagesWithName);
                }
            });
            
            if (colorImages.length > 0) {
                // Sort so default color images come first
                colorImages.sort((a, b) => {
                    if (a.isDefault && !b.isDefault) return -1;
                    if (!a.isDefault && b.isDefault) return 1;
                    return 0;
                });
                
                return { 
                    images: colorImages, 
                    type: 'color',
                    source: 'color images'
                };
            }
        }
        
        // No images available
        return { 
            images: [], 
            type: 'none',
            source: 'no images'
        };
    };

    if (loading) return (
        <div className="text-center p-4">
            <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">جاري التحميل...</span>
            </div>
            <p className="mt-2">جاري تحميل البيانات من الخادم...</p>
        </div>
    );

    return (
        <div className="container-fluid px-2 px-md-4">
            {/* Header */}
            <div className="row align-items-center mb-4">
                <div className="col-12 col-md-6">
                    <h2 className="mb-2 mb-md-0">إدارة المنتجات المستقلة</h2>
                </div>
                <div className="col-12 col-md-6">
                    <div className="d-flex flex-column flex-sm-row gap-2 justify-content-md-end">
                        <button 
                            className="btn btn-outline-secondary btn-sm"
                            onClick={fetchProducts}
                            disabled={loading}
                        >
                            <i className="fas fa-sync-alt me-1"></i>
                            {loading ? 'جاري التحديث...' : 'تحديث'}
                        </button>
                        <button 
                            className="btn btn-outline-info btn-sm"
                            onClick={() => setShowCategoryForm(true)}
                        >
                            <i className="fas fa-tags me-1"></i>
                            إدارة الفئات
                        </button>
                        <button 
                            className="btn btn-primary btn-sm"
                            onClick={() => setShowAddForm(true)}
                        >
                            <i className="fas fa-plus me-1"></i>
                            <span className="d-none d-sm-inline">إضافة منتج مستقل</span>
                            <span className="d-sm-none">إضافة</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Search Bar */}
            <div className="card mb-4">
                <div className="card-body">
                    <div className="row g-3">
                        <div className="col-md-8">
                            <div className="input-group">
                                <span className="input-group-text">
                                    <i className="fas fa-search"></i>
                                </span>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="البحث في المنتجات (الاسم، الوصف، الفئة، فئة البائع)..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="col-md-4">
                            <button 
                                className="btn btn-outline-secondary w-100"
                                onClick={() => setSearchQuery('')}
                                disabled={!searchQuery}
                            >
                                <i className="fas fa-times me-1"></i>
                                مسح البحث
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Category Management Form */}
            {showCategoryForm && (
                <div className="card mb-4">
                    <div className="card-header">
                        <h5>{editCategoryId ? 'تعديل الفئة' : 'إضافة فئة جديدة'}</h5>
                    </div>
                    <div className="card-body">
                        <form onSubmit={handleCategorySubmit}>
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">اسم الفئة</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={categoryForm.name}
                                        onChange={e => setCategoryForm({ ...categoryForm, name: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">صورة الفئة</label>
                                    <input
                                        type="file"
                                        className="form-control"
                                        accept="image/*"
                                        onChange={handleCategoryImageChange}
                                    />
                                </div>
                            </div>
                            <div className="d-flex gap-2">
                                <button type="submit" className="btn btn-success">
                                    {editCategoryId ? 'تحديث' : 'إضافة'}
                                </button>
                                <button 
                                    type="button" 
                                    className="btn btn-secondary"
                                    onClick={resetCategoryForm}
                                >
                                    إلغاء
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Categories List */}
            {showCategoryForm && (
                <div className="card mb-4">
                    <div className="card-header">
                        <h5>قائمة فئات المنتجات ({categories.length})</h5>
                    </div>
                    <div className="card-body">
                        <div className="table-responsive">
                            <table className="table table-striped">
                                <thead>
                                    <tr>
                                        <th>الصورة</th>
                                        <th>الاسم</th>
                                        <th>الإجراءات</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {categories.length === 0 ? (
                                        <tr>
                                            <td colSpan="3" className="text-center">لا توجد فئات</td>
                                        </tr>
                                    ) : (
                                        categories.map(category => (
                                            <tr key={category.id}>
                                                <td>
                                                    {category.imageUrl && (
                                                        <BlurredImage
                                                            src={getImageUrl(category.imageUrl)}
                                                            alt={category.name}
                                                            style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }}
                                                            fallbackUrl={`${(process.env.REACT_APP_API_URL || '').replace('/api', '')}/uploads/${category.imageUrl}`}
                                                        />
                                                    )}
                                                </td>
                                                <td>{category.name}</td>
                                                <td>
                                                    <div className="d-flex gap-2">
                                                        <button
                                                            className="btn btn-sm btn-outline-primary"
                                                            onClick={() => handleEditCategory(category)}
                                                        >
                                                            تعديل
                                                        </button>
                                                        <button
                                                            className="btn btn-sm btn-outline-danger"
                                                            onClick={() => handleDeleteCategory(category)}
                                                        >
                                                            حذف
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {showAddForm && (
                <div className="card mb-4">
                    <div className="card-header d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">
                            <i className="fas fa-box me-2"></i>
                            {editId ? 'تعديل المنتج المستقل' : 'إضافة منتج مستقل جديد'}
                        </h5>
                        <button 
                            className="btn-close" 
                            onClick={resetForm}
                            aria-label="إغلاق"
                        ></button>
                    </div>
                    <div className="card-body">
                        <div className="alert alert-info">
                            <i className="fas fa-info-circle me-2"></i>
                            <strong>إرشادات:</strong>
                            <ul className="mb-0 mt-2">
                                <li><strong>الفئة الفرعية:</strong> مطلوبة لتنظيم المنتج (مثل: هواتف ذكية، ملابس رجالية)</li>
                                <li><strong>الفئة الرئيسية:</strong> اختيارية لظهور المنتج في الصفحة الرئيسية (مثل: إلكترونيات، أزياء)</li>
                            </ul>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="row g-3">
                                <div className="col-lg-6 col-md-12">
                                    <label className="form-label fw-bold">اسم المنتج <span className="text-danger">*</span></label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="أدخل اسم المنتج"
                                        value={form.name}
                                        onChange={e => setForm({ ...form, name: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="col-lg-6 col-md-12">
                                    <label className="form-label fw-bold">الفئة الفرعية <span className="text-danger">*</span></label>
                                    <select
                                        className="form-select"
                                        value={form.productCategoryId}
                                        onChange={e => {
                                            console.log('AllProducts - Category selected:', e.target.value);
                                            setForm({ ...form, productCategoryId: e.target.value });
                                        }}
                                        required
                                    >
                                        <option value="">اختر الفئة الفرعية</option>
                                        {categories.map(category => (
                                            <option key={category.id} value={category.id}>
                                                {category.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-12">
                                    <label className="form-label fw-bold">الفئة الرئيسية (لظهور المنتج في الصفحة الرئيسية)</label>
                                    <select
                                        className="form-select"
                                        value={form.vendorCategoryId}
                                        onChange={e => {
                                            console.log('AllProducts - Vendor Category selected:', e.target.value);
                                            setForm({ ...form, vendorCategoryId: e.target.value });
                                        }}
                                    >
                                        <option value="">اختر الفئة الرئيسية (اختياري)</option>
                                        {Array.isArray(vendorCategories) && vendorCategories.map(vendorCategory => (
                                            <option key={vendorCategory.id} value={vendorCategory.id}>
                                                {vendorCategory.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-12">
                                    <label className="form-label fw-bold">وصف المنتج</label>
                                    <textarea
                                        className="form-control"
                                        rows="3"
                                        value={form.description}
                                        onChange={e => setForm({ ...form, description: e.target.value })}
                                        placeholder="أدخل وصف المنتج..."
                                    />
                                </div>
                                <div className="col-lg-6 col-md-12">
                                    <label className="form-label fw-bold">السعر (ل.س) <span className="text-danger">*</span></label>
                                    <div className="input-group">
                                        <input
                                            type="number"
                                            step="0.01"
                                            className="form-control"
                                            placeholder="0"
                                            min="0"
                                            value={form.priceInSYP}
                                            onChange={e => setForm({ ...form, priceInSYP: e.target.value })}
                                            required
                                        />
                                        <span className="input-group-text">ل.س</span>
                                    </div>
                                </div>
                                <div className="col-lg-6 col-md-12">
                                    <label className="form-label fw-bold">سعر التكلفة (ل.س)</label>
                                    <div className="input-group">
                                        <input
                                            type="number"
                                            step="0.01"
                                            className="form-control"
                                            placeholder="0"
                                            min="0"
                                            value={form.costPrice}
                                            onChange={e => setForm({ ...form, costPrice: e.target.value })}
                                        />
                                        <span className="input-group-text">ل.س</span>
                                    </div>
                                </div>
                                <div className="col-lg-6 col-md-12">
                                    <label className="form-label fw-bold">سعر الخصم (اختياري)</label>
                                    <div className="input-group">
                                        <input
                                            type="number"
                                            step="0.01"
                                            className="form-control"
                                            placeholder="0"
                                            min="0"
                                            value={form.discountPrice}
                                            onChange={e => setForm({ ...form, discountPrice: e.target.value, discountPercentage: e.target.value ? '' : form.discountPercentage })}
                                        />
                                        <span className="input-group-text">ل.س</span>
                                    </div>
                                </div>
                                <div className="col-lg-6 col-md-12">
                                    <label className="form-label fw-bold">نسبة الخصم % (اختياري)</label>
                                    <div className="input-group">
                                        <input
                                            type="number"
                                            step="0.01"
                                            className="form-control"
                                            placeholder="0"
                                            min="0"
                                            max="99"
                                            value={form.discountPercentage}
                                            onChange={e => setForm({ ...form, discountPercentage: e.target.value, discountPrice: e.target.value ? '' : form.discountPrice })}
                                        />
                                        <span className="input-group-text">%</span>
                                    </div>
                                </div>
                                <div className="col-lg-6 col-md-12">
                                    <label className="form-label fw-bold">بداية الخصم</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={form.discountStartDate}
                                        onChange={e => setForm({ ...form, discountStartDate: e.target.value })}
                                    />
                                </div>
                                <div className="col-lg-6 col-md-12">
                                    <label className="form-label fw-bold">نهاية الخصم</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={form.discountEndDate}
                                        onChange={e => setForm({ ...form, discountEndDate: e.target.value })}
                                    />
                                </div>
                                {(form.discountPrice || form.discountPercentage) && Number(form.priceInSYP || 0) > 0 && (
                                    <div className="col-12">
                                        {(() => {
                                            const original = Number(form.priceInSYP || 0);
                                            const final = form.discountPrice
                                                ? Number(form.discountPrice)
                                                : Math.round(original * (1 - (Number(form.discountPercentage || 0) / 100)));
                                            const percent = original > 0 ? Math.round(((original - final) / original) * 100) : 0;
                                            return (
                                                <div className="alert alert-warning mb-0">
                                                    السعر الأصلي: <strong>{formatNumber(original)} ل.س</strong>
                                                    {'  '}→{'  '}
                                                    السعر بعد الخصم: <strong>{formatNumber(final)} ل.س</strong>
                                                    {'  '}({percent}%)
                                                </div>
                                            );
                                        })()}
                                    </div>
                                )}
                                <div className="col-lg-6 col-md-12">
                                    <label className="form-label fw-bold">وقت التوصيل (بالدقائق)</label>
                                    <div className="input-group">
                                        <input
                                            type="number"
                                            className="form-control"
                                            placeholder="30"
                                            min="0"
                                            value={form.deliveryTimeMinutes}
                                            onChange={e => setForm({ ...form, deliveryTimeMinutes: e.target.value })}
                                        />
                                        <span className="input-group-text">دقيقة</span>
                                    </div>
                                </div>
                                <div className="col-12">
                                    <label className="form-label fw-bold">صور المنتج</label>
                                    <input
                                        type="file"
                                        className="form-control mb-2"
                                        accept="image/*"
                                        multiple
                                        onChange={handleMultipleImagesChange}
                                    />
                                    <div className="form-text mb-3">
                                        <i className="fas fa-info-circle me-1"></i>
                                        يمكنك اختيار صورة واحدة أو عدة صور. سيتم ضغط الصور تلقائياً لتحسين الأداء. الصورة الأولى ستكون الصورة الرئيسية.
                                    </div>
                                    
                                    {/* Display selected images */}
                                    {form.imageFiles.length > 0 && (
                                        <div className="row g-2">
                                            {form.imageFiles.map((file, index) => (
                                                <div key={index} className="col-6 col-md-4 col-lg-3">
                                                    <div className="card h-100">
                                                        <div className="position-relative">
                                                            <img
                                                                src={URL.createObjectURL(file)}
                                                                alt={`Preview ${index + 1}`}
                                                                className="card-img-top"
                                                                style={{ height: '120px', objectFit: 'cover' }}
                                                            />
                                                            {index === 0 && (
                                                                <span className="position-absolute top-0 start-0 badge bg-primary m-1">
                                                                    <i className="fas fa-star me-1"></i>
                                                                    رئيسية
                                                                </span>
                                                            )}
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1"
                                                                onClick={() => removeImage(index)}
                                                                title="حذف الصورة"
                                                            >
                                                                <i className="fas fa-times"></i>
                                                            </button>
                                                        </div>
                                                        <div className="card-body p-2">
                                                            <small className="text-muted d-block text-truncate">
                                                                {file.name}
                                                            </small>
                                                            <small className="text-muted">
                                                                {(file.size / 1024).toFixed(1)} ك.ب
                                                            </small>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Display existing images when editing */}
                                    {editId && form.existingImages && form.existingImages.length > 0 && (
                                        <div className="mb-3">
                                            <h6 className="fw-bold text-info mb-2">
                                                <i className="fas fa-images me-1"></i>
                                                الصور الحالية
                                            </h6>
                                            <div className="row g-2">
                                                {form.existingImages.map((image, index) => (
                                                    <div key={image.id} className="col-6 col-md-4 col-lg-3">
                                                        <div className="card h-100">
                                                            <div className="position-relative">
                                                                <BlurredImage
                                                                    src={getImageUrl(image.imageUrl)}
                                                                    alt={`Existing ${index + 1}`}
                                                                    className="card-img-top"
                                                                    style={{ height: '120px', objectFit: 'cover' }}
                                                                    fallbackUrl={`${(process.env.REACT_APP_API_URL || '').replace('/api', '')}/uploads/${image.imageUrl}`}
                                                                />
                                                                {image.isPrimary && (
                                                                    <span className="position-absolute top-0 start-0 badge bg-success m-1">
                                                                        <i className="fas fa-star me-1"></i>
                                                                        رئيسية
                                                                    </span>
                                                                )}
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1"
                                                                    onClick={() => deleteExistingImage(image.id)}
                                                                    title="حذف الصورة الحالية"
                                                                >
                                                                    <i className="fas fa-trash"></i>
                                                                </button>
                                                            </div>
                                                            <div className="card-body p-2">
                                                                <small className="text-success d-block">
                                                                    <i className="fas fa-database me-1"></i>
                                                                    صورة محفوظة
                                                                </small>
                                                                <small className="text-muted">
                                                                    ترتيب: {image.order || 'غير محدد'}
                                                                </small>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Product Colors Section */}
                                <div className="col-12">
                                    <div className="card border-0 bg-light">
                                        <div className="card-header bg-transparent border-0 d-flex justify-content-between align-items-center">
                                            <h6 className="mb-0 fw-bold">
                                                <i className="fas fa-palette me-2"></i>
                                                ألوان المنتج (اختياري)
                                            </h6>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-primary"
                                                onClick={addNewColor}
                                            >
                                                <i className="fas fa-plus me-1"></i>
                                                إضافة لون
                                            </button>
                                        </div>
                                        <div className="card-body">
                                            {form.colors && form.colors.length === 0 ? (
                                                <div className="text-center text-muted py-3">
                                                    <i className="fas fa-palette fa-2x mb-2"></i>
                                                    <p className="mb-0">لم يتم إضافة ألوان للمنتج بعد</p>
                                                    <small>يمكنك إضافة ألوان مختلفة للمنتج مع صور خاصة لكل لون</small>
                                                </div>
                                            ) : (
                                                <div className="row g-3">
                                                    {form.colors && form.colors.map((color, colorIndex) => (
                                                        <div key={color.id} className="col-lg-6 col-12">
                                                            <div className={`border rounded p-3 ${color.isDefault ? 'border-primary bg-primary bg-opacity-10' : ''}`}>
                                                                <div className="d-flex justify-content-between align-items-center mb-3">
                                                                    <h6 className="mb-0">
                                                                        اللون {colorIndex + 1}
                                                                        {color.isDefault && <span className="badge bg-primary ms-2">افتراضي</span>}
                                                                    </h6>
                                                                    <div className="btn-group btn-group-sm">
                                                                        {!color.isDefault && (
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-outline-primary"
                                                                                onClick={() => setDefaultColor(colorIndex)}
                                                                                title="تعيين كلون افتراضي"
                                                                            >
                                                                                <i className="fas fa-star"></i>
                                                                            </button>
                                                                        )}
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-outline-danger"
                                                                            onClick={(e) => {
                                                                                e.preventDefault();
                                                                                e.stopPropagation();
                                                                                removeColor(colorIndex);
                                                                            }}
                                                                            title="حذف اللون"
                                                                        >
                                                                            <i className="fas fa-trash"></i>
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                                
                                                                <div className="row g-2 mb-3">
                                                                    <div className="col-md-6">
                                                                        <label className="form-label fw-bold">اسم اللون</label>
                                                                        <input
                                                                            type="text"
                                                                            className="form-control"
                                                                            placeholder="مثال: أحمر، أزرق، أسود"
                                                                            value={color.colorName}
                                                                            onChange={(e) => updateColor(colorIndex, 'colorName', e.target.value)}
                                                                        />
                                                                    </div>
                                                                    <div className="col-md-6">
                                                                        <label className="form-label fw-bold">كود اللون (اختياري)</label>
                                                                        <div className="input-group">
                                                                            <input
                                                                                type="color"
                                                                                className="form-control form-control-color"
                                                                                value={color.colorHex || '#000000'}
                                                                                onChange={(e) => updateColor(colorIndex, 'colorHex', e.target.value)}
                                                                                style={{ maxWidth: '60px' }}
                                                                            />
                                                                            <input
                                                                                type="text"
                                                                                className="form-control"
                                                                                placeholder="#000000"
                                                                                value={color.colorHex || ''}
                                                                                onChange={(e) => updateColor(colorIndex, 'colorHex', e.target.value)}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                {/* Color Images */}
                                                                <div>
                                                                    <label className="form-label fw-bold">صور هذا اللون</label>
                                                                    <input
                                                                        type="file"
                                                                        className="form-control mb-2"
                                                                        accept="image/*"
                                                                        multiple
                                                                        onChange={(e) => addColorImages(colorIndex, Array.from(e.target.files || []))}
                                                                    />
                                                                    
                                                                    {color.images && color.images.length > 0 && (
                                                                        <div className="row g-2 mt-2">
                                                                            {color.images && color.images.map((image, imageIndex) => (
                                                                                <div key={imageIndex} className="col-6 col-md-3 col-lg-2">
                                                                                    <div className="card">
                                                                                        <div className="position-relative">
                                                                                            <img
                                                                                                src={typeof image === 'string' ? image : URL.createObjectURL(image)}
                                                                                                alt={`${color.colorName} - ${imageIndex + 1}`}
                                                                                                className="card-img-top"
                                                                                                style={{ height: '80px', objectFit: 'cover' }}
                                                                                            />
                                                                                            <button
                                                                                                type="button"
                                                                                                className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1"
                                                                                                onClick={() => removeColorImage(colorIndex, imageIndex)}
                                                                                                title="حذف الصورة"
                                                                                                style={{ fontSize: '0.7rem', padding: '0.125rem 0.25rem' }}
                                                                                            >
                                                                                                <i className="fas fa-times"></i>
                                                                                            </button>
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                    
                                                                    {(!color.images || color.images.length === 0) && (
                                                                        <div className="text-center text-muted py-2 border border-dashed rounded">
                                                                            <i className="fas fa-images mb-1"></i>
                                                                            <small className="d-block">لم يتم إضافة صور لهذا اللون</small>
                                                                        </div>
                                                                    )}
                                                                </div>

                                                                {/* Color Sizes Section */}
                                                                <div className="mt-3">
                                                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                                                        <label className="form-label fw-bold mb-0">
                                                                            <i className="fas fa-ruler-combined me-1"></i>
                                                                            مقاسات هذا اللون (اختياري)
                                                                        </label>
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-sm btn-outline-info"
                                                                            onClick={() => addColorSize(colorIndex)}
                                                                        >
                                                                            <i className="fas fa-plus me-1"></i>
                                                                            إضافة مقاس
                                                                        </button>
                                                                    </div>
                                                                    
                                                                    {color.sizes && color.sizes.length > 0 ? (
                                                                        <div className="row g-2">
                                                                            {color.sizes.map((size, sizeIndex) => (
                                                                                <div key={size.id || `new-color-size-${sizeIndex}`} className="col-12">
                                                                                    <div className={`card ${size.isDefault ? 'border-info bg-info bg-opacity-10' : ''}`}>
                                                                                        <div className="card-body p-2">
                                                                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                                                                <small className="fw-bold">
                                                                                                    مقاس {sizeIndex + 1}
                                                                                                    {size.isDefault && <span className="badge bg-info ms-1">افتراضي</span>}
                                                                                                </small>
                                                                                                <div className="btn-group btn-group-sm">
                                                                                                    {!size.isDefault && (
                                                                                                        <button
                                                                                                            type="button"
                                                                                                            className="btn btn-outline-info btn-sm"
                                                                                                            onClick={() => setDefaultColorSize(colorIndex, sizeIndex)}
                                                                                                            title="تعيين كمقاس افتراضي لهذا اللون"
                                                                                                        >
                                                                                                            <i className="fas fa-star"></i>
                                                                                                        </button>
                                                                                                    )}
                                                                                                    <button
                                                                                                        type="button"
                                                                                                        className="btn btn-outline-danger btn-sm"
                                                                                                        onClick={() => removeColorSize(colorIndex, sizeIndex)}
                                                                                                        title="حذف المقاس"
                                                                                                    >
                                                                                                        <i className="fas fa-trash"></i>
                                                                                                    </button>
                                                                                                </div>
                                                                                            </div>
                                                                                            <div className="row g-1">
                                                                                                <div className="col-4">
                                                                                                    <input
                                                                                                        type="text"
                                                                                                        className="form-control form-control-sm"
                                                                                                        placeholder="اسم المقاس"
                                                                                                        value={size.sizeName}
                                                                                                        onChange={(e) => updateColorSize(colorIndex, sizeIndex, 'sizeName', e.target.value)}
                                                                                                    />
                                                                                                </div>
                                                                                                <div className="col-3">
                                                                                                    <input
                                                                                                        type="text"
                                                                                                        className="form-control form-control-sm"
                                                                                                        placeholder="كود (S, M, L)"
                                                                                                        value={size.sizeCode}
                                                                                                        onChange={(e) => updateColorSize(colorIndex, sizeIndex, 'sizeCode', e.target.value)}
                                                                                                    />
                                                                                                </div>
                                                                                                <div className="col-3">
                                                                                                    <input
                                                                                                        type="number"
                                                                                                        className="form-control form-control-sm"
                                                                                                        placeholder="ترتيب"
                                                                                                        value={size.order}
                                                                                                        onChange={(e) => updateColorSize(colorIndex, sizeIndex, 'order', parseInt(e.target.value) || 1)}
                                                                                                    />
                                                                                                </div>
                                                                                                <div className="col-12">
                                                                                                    <input
                                                                                                        type="text"
                                                                                                        className="form-control form-control-sm"
                                                                                                        placeholder="وصف المقاس (اختياري)"
                                                                                                        value={size.sizeDescription}
                                                                                                        onChange={(e) => updateColorSize(colorIndex, sizeIndex, 'sizeDescription', e.target.value)}
                                                                                                    />
                                                                                                </div>
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    ) : (
                                                                        <div className="text-center text-muted py-2 border border-dashed rounded">
                                                                            <i className="fas fa-ruler-combined mb-1"></i>
                                                                            <small className="d-block">لم يتم إضافة مقاسات لهذا اللون</small>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Product Sizes Section */}
                                <div className="col-12">
                                    <div className="card border-0 shadow-sm">
                                        <div className="card-header bg-transparent border-0 d-flex justify-content-between align-items-center">
                                            <h6 className="mb-0 fw-bold">
                                                <i className="fas fa-ruler-combined me-2"></i>
                                                أحجام المنتج (اختياري)
                                            </h6>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-success"
                                                onClick={addNewSize}
                                            >
                                                <i className="fas fa-plus me-1"></i>
                                                إضافة حجم
                                            </button>
                                        </div>
                                        <div className="card-body">
                                            {form.sizes && form.sizes.length === 0 ? (
                                                <div className="text-center text-muted py-3">
                                                    <i className="fas fa-ruler-combined fa-2x mb-2"></i>
                                                    <p className="mb-0">لم يتم إضافة أحجام للمنتج بعد</p>
                                                    <small>يمكنك إضافة أحجام مختلفة للمنتج (S, M, L, XL, إلخ)</small>
                                                </div>
                                            ) : (
                                                <div className="row g-3">
                                                    {form.sizes && form.sizes.map((size, sizeIndex) => (
                                                        <div key={size.id || `new-size-${sizeIndex}`} className="col-lg-6 col-12">
                                                            <div className={`border rounded p-3 ${size.isDefault ? 'border-success bg-success bg-opacity-10' : ''}`}>
                                                                <div className="d-flex justify-content-between align-items-center mb-3">
                                                                    <h6 className="mb-0">
                                                                        الحجم {sizeIndex + 1}
                                                                        {size.isDefault && <span className="badge bg-success ms-2">افتراضي</span>}
                                                                    </h6>
                                                                    <div className="btn-group btn-group-sm">
                                                                        {/* Move Up Button */}
                                                                        {sizeIndex > 0 && (
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-outline-secondary"
                                                                                onClick={() => moveSizeUp(sizeIndex)}
                                                                                title="نقل للأعلى"
                                                                            >
                                                                                <i className="fas fa-arrow-up"></i>
                                                                            </button>
                                                                        )}
                                                                        {/* Move Down Button */}
                                                                        {sizeIndex < (form.sizes?.length || 0) - 1 && (
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-outline-secondary"
                                                                                onClick={() => moveSizeDown(sizeIndex)}
                                                                                title="نقل للأسفل"
                                                                            >
                                                                                <i className="fas fa-arrow-down"></i>
                                                                            </button>
                                                                        )}
                                                                        {/* Set Default Button */}
                                                                        {!size.isDefault && (
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-outline-success"
                                                                                onClick={() => setDefaultSize(sizeIndex)}
                                                                                title="تعيين كحجم افتراضي"
                                                                            >
                                                                                <i className="fas fa-star"></i>
                                                                            </button>
                                                                        )}
                                                                        {/* Delete Button */}
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-outline-danger"
                                                                            onClick={(e) => {
                                                                                e.preventDefault();
                                                                                e.stopPropagation();
                                                                                removeSize(sizeIndex);
                                                                            }}
                                                                            title="حذف الحجم"
                                                                        >
                                                                            <i className="fas fa-trash"></i>
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                                
                                                                <div className="row g-2 mb-2">
                                                                    <div className="col-md-6">
                                                                        <label className="form-label fw-bold">اسم الحجم *</label>
                                                                        <input
                                                                            type="text"
                                                                            className="form-control"
                                                                            placeholder="مثال: كبير، وسط، صغير"
                                                                            value={size.sizeName}
                                                                            onChange={(e) => updateSize(sizeIndex, 'sizeName', e.target.value)}
                                                                        />
                                                                    </div>
                                                                    <div className="col-md-6">
                                                                        <label className="form-label fw-bold">كود الحجم</label>
                                                                        <input
                                                                            type="text"
                                                                            className="form-control"
                                                                            placeholder="مثال: L, M, S"
                                                                            value={size.sizeCode}
                                                                            onChange={(e) => updateSize(sizeIndex, 'sizeCode', e.target.value)}
                                                                        />
                                                                    </div>
                                                                </div>
                                                                
                                                                <div className="mb-0">
                                                                    <label className="form-label fw-bold">وصف الحجم</label>
                                                                    <textarea
                                                                        className="form-control"
                                                                        placeholder="وصف تفصيلي للحجم (اختياري)"
                                                                        rows={2}
                                                                        value={size.sizeDescription}
                                                                        onChange={(e) => updateSize(sizeIndex, 'sizeDescription', e.target.value)}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="d-flex flex-column flex-sm-row gap-2 justify-content-end mt-4">
                                <button type="button" className="btn btn-outline-secondary" onClick={resetForm}>
                                    <i className="fas fa-times me-1"></i>
                                    إلغاء
                                </button>
                                <button type="submit" className="btn btn-primary">
                                    <i className="fas fa-save me-1"></i>
                                    {editId ? 'تحديث المنتج' : 'إضافة المنتج'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <div className="card">
                <div className="card-header">
                    <div className="row align-items-center">
                        <div className="col-sm-6">
                            <h5 className="mb-0">قائمة المنتجات المستقلة ({filteredProducts.length} من {products.length})</h5>
                        </div>
                        <div className="col-sm-6 text-end">
                            <small className="text-muted d-block">
                                آخر تحديث: {formatTime(new Date())}
                            </small>
                            {searchQuery && <span className="badge bg-info mt-1">البحث: "{searchQuery}"</span>}
                        </div>
                    </div>
                </div>
                <div className="card-body">
                    {filteredProducts.length === 0 ? (
                        <div className="text-center py-5">
                            <i className="fas fa-box-open fa-3x text-muted mb-3"></i>
                            <h5 className="text-muted">
                                {searchQuery ? 'لا توجد منتجات تطابق البحث' : 'لا توجد منتجات مستقلة'}
                            </h5>
                            {!searchQuery && (
                                <p className="text-muted mb-3">ابدأ بإضافة منتج مستقل جديد</p>
                            )}
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-hover">
                                <thead className="table-light">
                                    <tr>
                                        <th style={{ width: '80px' }}>الصورة</th>
                                        <th>الاسم</th>
                                                <th className="d-none d-md-table-cell">الوصف</th>
                                                <th className="d-none d-lg-table-cell">الألوان</th>
                                                <th className="d-none d-lg-table-cell">الأحجام</th>
                                                <th className="d-none d-lg-table-cell">الفئة</th>
                                                <th className="d-none d-lg-table-cell">فئة البائع</th>
                                                <th>السعر</th>
                                                <th className="d-none d-md-table-cell">التكلفة</th>
                                                <th>الحالة</th>
                                                <th style={{ width: '120px' }}>الإجراءات</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredProducts.map(product => (
                                                <tr key={`standalone-list-${product.id}`}>
                                                    <td>
                                                        {/* Smart Image Display - Product images or Color images as fallback */}
                                                        {(() => {
                                                            const displayData = getDisplayImages(product);
                                                            const images = displayData.images;
                                                            
                                                            if (images.length > 0) {
                                                                return (
                                                                    <div 
                                                                        className="d-flex gap-1 scrollable-images" 
                                                                        style={{ 
                                                                            width: '120px', 
                                                                            overflowX: 'auto', 
                                                                            overflowY: 'hidden'
                                                                        }}
                                                                    >
                                                                        {images.map((img, index) => (
                                                                            <div key={img.id || `img-${index}`} className="flex-shrink-0 position-relative">
                                                                                <BlurredImage
                                                                                    src={getImageUrl(img.imageUrl)}
                                                                                    alt={displayData.type === 'color' ? 
                                                                                        `${product.name} - ${img.colorName}` : 
                                                                                        `${product.name} - ${index + 1}`
                                                                                    }
                                                                                    className="rounded"
                                                                                    style={{ 
                                                                                        width: 45, 
                                                                                        height: 45, 
                                                                                        objectFit: 'cover',
                                                                                        border: img.isPrimary ? '2px solid #0d6efd' : 
                                                                                               (displayData.type === 'color' && img.isDefault) ? '2px solid #28a745' : 
                                                                                               '1px solid #dee2e6'
                                                                                    }}
                                                                                    fallbackUrl={`${(process.env.REACT_APP_API_URL || '').replace('/api', '')}/uploads/${img.imageUrl}`}
                                                                                />
                                                                                {/* Show indicator for image type */}
                                                                                {img.isPrimary && displayData.type === 'product' && (
                                                                                    <span 
                                                                                        className="position-absolute top-0 start-0 badge bg-primary"
                                                                                        style={{ fontSize: '0.6rem', transform: 'translate(-30%, -30%)' }}
                                                                                        title="صورة رئيسية"
                                                                                    >
                                                                                        <i className="fas fa-star"></i>
                                                                                    </span>
                                                                                )}
                                                                                {displayData.type === 'color' && img.isDefault && (
                                                                                    <span 
                                                                                        className="position-absolute top-0 start-0 badge bg-success"
                                                                                        style={{ fontSize: '0.6rem', transform: 'translate(-30%, -30%)' }}
                                                                                        title={`لون افتراضي: ${img.colorName}`}
                                                                                    >
                                                                                        <i className="fas fa-palette"></i>
                                                                                    </span>
                                                                                )}
                                                                                {displayData.type === 'color' && img.colorHex && (
                                                                                    <div
                                                                                        className="position-absolute bottom-0 end-0"
                                                                                        style={{
                                                                                            width: '12px',
                                                                                            height: '12px',
                                                                                            backgroundColor: img.colorHex,
                                                                                            border: '1px solid #fff',
                                                                                            borderRadius: '50%',
                                                                                            transform: 'translate(25%, 25%)'
                                                                                        }}
                                                                                        title={img.colorName}
                                                                                    />
                                                                                )}
                                                                            </div>
                                                                        ))}
                                                                        {/* Show indicator of image source */}
                                                                        {displayData.type === 'color' && (
                                                                            <div className="d-flex align-items-center ms-1">
                                                                                <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                                                                                    <i className="fas fa-palette me-1"></i>
                                                                                    ألوان
                                                                                </small>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                );
                                                            } else {
                                                                return (
                                                                    /* No image placeholder */
                                                                    <div 
                                                                        className="d-flex align-items-center justify-content-center bg-light rounded"
                                                                        style={{ width: 50, height: 50 }}
                                                                    >
                                                                        <i className="fas fa-image text-muted"></i>
                                                                    </div>
                                                                );
                                                            }
                                                        })()}
                                                    </td>
                                                    <td>
                                                        <div className="fw-bold">{product.name}</div>
                                                        <div className="d-md-none">
                                                            <small className="text-muted">
                                                                {product.description ? 
                                                                    (product.description.length > 50 ? 
                                                                        product.description.substring(0, 50) + '...' : 
                                                                        product.description
                                                                    ) : 'لا يوجد وصف'
                                                                }
                                                            </small>
                                                        </div>
                                                        <div className="d-lg-none mt-1">
                                                            <span className="badge bg-primary me-1">{product.productCategory?.name || 'بدون فئة'}</span>
                                                            {product.vendorCategory?.name && (
                                                                <span className="badge bg-info">{product.vendorCategory.name}</span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="d-none d-md-table-cell">
                                                        <div style={{ maxWidth: '200px', fontSize: '0.875rem' }}>
                                                            {product.description ? 
                                                                (product.description.length > 100 ? 
                                                                    product.description.substring(0, 100) + '...' : 
                                                                    product.description
                                                                ) : '—'
                                                            }
                                                        </div>
                                                    </td>
                                                    <td className="d-none d-lg-table-cell">
                                                        {product.productColors && product.productColors.length > 0 ? (
                                                            <div className="d-flex flex-wrap gap-1">
                                                                {product.productColors.map((color, index) => (
                                                                    <div key={color.id} className="d-flex align-items-center">
                                                                        {color.colorHex && (
                                                                            <div
                                                                                className="rounded-circle me-1"
                                                                                style={{
                                                                                    width: '12px',
                                                                                    height: '12px',
                                                                                    backgroundColor: color.colorHex,
                                                                                    border: '1px solid #dee2e6'
                                                                                }}
                                                                            ></div>
                                                                        )}
                                                                        <span className={`badge ${color.isDefault ? 'bg-success' : 'bg-secondary'} me-1`}>
                                                                            {color.colorName}
                                                                            {color.isDefault && <i className="fas fa-star ms-1" style={{fontSize: '0.6rem'}}></i>}
                                                                        </span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ) : '—'}
                                                    </td>
                                                    <td className="d-none d-lg-table-cell">
                                                        {product.productSizes && product.productSizes.length > 0 ? (
                                                            <div className="d-flex flex-wrap gap-1">
                                                                {product.productSizes
                                                                    .sort((a, b) => (a.order || 1) - (b.order || 1))
                                                                    .map((size, index) => (
                                                                        <span key={size.id} className={`badge ${size.isDefault ? 'bg-success' : 'bg-secondary'} me-1`}>
                                                                            {size.sizeCode || size.sizeName}
                                                                            {size.isDefault && <i className="fas fa-star ms-1" style={{fontSize: '0.6rem'}}></i>}
                                                                        </span>
                                                                    ))}
                                                            </div>
                                                        ) : '—'}
                                                    </td>
                                                    <td className="d-none d-lg-table-cell">
                                                        <span className="badge bg-primary">{product.productCategory?.name || '—'}</span>
                                                    </td>
                                                    <td className="d-none d-lg-table-cell">
                                                        {product.vendorCategory?.name ? (
                                                            <span className="badge bg-info">{product.vendorCategory.name}</span>
                                                        ) : '—'}
                                                    </td>
                                                    <td>
                                                        {product.hasActiveDiscount && product.finalPrice < product.originalPrice ? (
                                                            <>
                                                                <div className="fw-bold text-danger">
                                                                    {Number(product.finalPrice || 0).toFixed(0)} ل.س
                                                                </div>
                                                                <div>
                                                                    <small className="text-muted text-decoration-line-through">
                                                                        {Number(product.originalPrice || product.priceInSYP || 0).toFixed(0)} ل.س
                                                                    </small>
                                                                </div>
                                                                <div>
                                                                    <span className="badge bg-warning text-dark">
                                                                        خصم {Math.round(Number(product.discountPercentage || 0))}%
                                                                    </span>
                                                                </div>
                                                            </>
                                                        ) : (
                                                            <div className="fw-bold text-success">{product.priceInSYP?.toFixed(0)} ل.س</div>
                                                        )}
                                                        <div className="d-md-none">
                                                            <small className="text-muted">التكلفة: {product.costPrice?.toFixed(0) || '—'} ل.س</small>
                                                        </div>
                                                    </td>
                                                    <td className="d-none d-md-table-cell">
                                                        <span className="fw-bold text-primary">{product.costPrice?.toFixed(0) || '—'} ل.س</span>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex flex-column gap-1">
                                                            <span className={`badge ${product.isAvailable !== false ? 'bg-success' : 'bg-danger'} align-self-start`}>
                                                                {product.isAvailable !== false ? 'متاح' : 'غير متاح'}
                                                            </span>
                                                            <button
                                                                className={`btn btn-sm ${product.isAvailable !== false ? 'btn-warning' : 'btn-success'}`}
                                                                onClick={() => handleToggleAvailability(product)}
                                                                title={product.isAvailable !== false ? 'جعل المنتج غير متاح' : 'جعل المنتج متاح'}
                                                            >
                                                                {product.isAvailable !== false ? 'جعل المنتج غير متاح' : 'جعل المنتج متاح'}
                                                            </button>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="btn-group btn-group-sm">
                                                            <button
                                                                className="btn btn-outline-primary"
                                                                onClick={() => handleEdit(product)}
                                                                title="تعديل"
                                                            >
                                                                <i className="fas fa-edit"></i>
                                                                <span className="d-none d-sm-inline ms-1">تعديل</span>
                                                            </button>
                                                            <button
                                                                className="btn btn-outline-danger"
                                                                onClick={() => handleDelete(product)}
                                                                title="حذف"
                                                            >
                                                                <i className="fas fa-trash"></i>
                                                                <span className="d-none d-sm-inline ms-1">حذف</span>
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default AllProducts;
